import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Car, 
  DollarSign, 
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Calculator,
  Shield,
  Clock,
  Users,
  Building2,
  CreditCard,
  PiggyBank,
  GraduationCap,
  Briefcase,
  Heart,
  Star,
  Phone,
  FileText,
  Award,
  Target,
  Lightbulb,
  Handshake,
  Percent,
  Calendar,
  MapPin
} from 'lucide-react';

const Loans = () => {
  const [calculatorValues, setCalculatorValues] = useState({
    amount: '',
    rate: '',
    term: '',
    type: ''
  });

  const loanTypes = [
    {
      icon: Home,
      title: "Home Mortgages",
      description: "Make homeownership dreams come true with competitive rates",
      rate: "Starting at 6.25% APR",
      maxAmount: "Up to $2M",
      terms: "15-30 years",
      features: [
        "First-time buyer programs",
        "VA and FHA loans available",
        "Pre-approval in 24 hours",
        "Local underwriting decisions",
        "Closing cost assistance programs",
        "Refinancing and cash-out options",
        "Fixed and adjustable rates",
        "No prepayment penalties"
      ],
      bgColor: "from-blue-500 to-blue-600",
      popular: true
    },
    {
      icon: Car,
      title: "Auto Loans",
      description: "Drive away with confidence and affordable payments",
      rate: "Starting at 4.99% APR",
      maxAmount: "Up to $150k",
      terms: "12-84 months",
      features: [
        "New and used vehicle financing",
        "Same-day approval available",
        "Direct dealer relationships",
        "No application fees",
        "Flexible payment schedules",
        "Refinancing options available",
        "Gap insurance available",
        "Pre-approval shopping"
      ],
      bgColor: "from-green-500 to-green-600",
      popular: false
    },
    {
      icon: DollarSign,
      title: "Personal Loans",
      description: "Achieve your goals with flexible unsecured financing",
      rate: "Starting at 7.99% APR",
      maxAmount: "Up to $50k",
      terms: "12-84 months",
      features: [
        "No collateral required",
        "Fixed interest rates",
        "Quick funding in 1-2 days",
        "Debt consolidation",
        "Home improvements",
        "Medical expenses",
        "Wedding and vacation funding",
        "Flexible repayment terms"
      ],
      bgColor: "from-purple-500 to-purple-600",
      popular: false
    },
    {
      icon: TrendingUp,
      title: "Home Equity Solutions",
      description: "Unlock your home's value for major expenses",
      rate: "Starting at 5.75% APR",
      maxAmount: "Up to 90% LTV",
      terms: "5-30 years",
      features: [
        "Home equity loans (fixed rate)",
        "Home equity line of credit (HELOC)",
        "Tax advantages may apply",
        "Use for any purpose",
        "Interest-only payment options",
        "Online account management",
        "Rate lock options",
        "No annual fees on HELOC"
      ],
      bgColor: "from-orange-500 to-orange-600",
      popular: false
    }
  ];

  const specialtyLoans = [
    {
      icon: Briefcase,
      title: "Business Loans",
      description: "Fuel your business growth and expansion",
      rate: "Starting at 6.99% APR",
      features: ["Equipment financing", "Working capital", "Commercial real estate", "SBA loans"]
    },
    {
      icon: GraduationCap,
      title: "Student Loans",
      description: "Invest in education and future success",
      rate: "Starting at 5.99% APR",
      features: ["Undergraduate loans", "Graduate school financing", "Parent PLUS alternatives", "Refinancing options"]
    },
    {
      icon: Building2,
      title: "Construction Loans",
      description: "Build your dream home from the ground up",
      rate: "Starting at 7.25% APR",
      features: ["Custom home construction", "Major renovations", "Lot purchases", "Bridge financing"]
    },
    {
      icon: PiggyBank,
      title: "Savings-Secured Loans",
      description: "Build credit while keeping your savings intact",
      rate: "Starting at 3.99% APR",
      features: ["Build credit history", "Keep earning on savings", "Low rates", "Flexible terms"]
    }
  ];

  const loanBenefits = [
    {
      icon: Clock,
      title: "Quick Decisions",
      description: "Get pre-approved in minutes with same-day decisions on most loans"
    },
    {
      icon: Users,
      title: "Local Experts",
      description: "Work with experienced loan officers who understand your community"
    },
    {
      icon: Shield,
      title: "Competitive Rates",
      description: "Enjoy some of the most competitive rates in the region"
    },
    {
      icon: Handshake,
      title: "Personal Service",
      description: "Receive dedicated support throughout the entire loan process"
    },
    {
      icon: Award,
      title: "Award-Winning Service",
      description: "Recognized excellence in customer satisfaction and loan services"
    },
    {
      icon: Target,
      title: "Flexible Terms",
      description: "Customizable loan terms to fit your budget and timeline"
    }
  ];

  const applicationSteps = [
    {
      step: "1",
      title: "Apply Online",
      description: "Complete our secure online application in minutes"
    },
    {
      step: "2",
      title: "Quick Review",
      description: "Our loan experts review your application within 24 hours"
    },
    {
      step: "3",
      title: "Get Approved",
      description: "Receive your approval decision and loan terms"
    },
    {
      step: "4",
      title: "Close & Fund",
      description: "Finalize paperwork and receive your funds quickly"
    }
  ];

  const testimonials = [
    {
      name: "Jennifer Martinez",
      location: "Metro City",
      loanType: "Home Mortgage",
              content: "First Capital International made buying our first home so easy. Their team guided us through every step and got us an amazing rate!",
      rating: 5
    },
    {
      name: "Mike Chen",
      location: "Westside",
      loanType: "Auto Loan",
      content: "Got my car loan approved the same day I applied. The process was incredibly smooth and the rate was better than my credit union.",
      rating: 5
    },
    {
      name: "Sarah Johnson",
      location: "Downtown",
      loanType: "Personal Loan",
      content: "Used their personal loan for home improvements. Fast approval, great rate, and flexible payments that fit our budget perfectly.",
      rating: 5
    }
  ];

  const calculatePayment = () => {
    if (!calculatorValues.amount || !calculatorValues.rate || !calculatorValues.term) return 0;
    
    const principal = parseFloat(calculatorValues.amount);
    const monthlyRate = parseFloat(calculatorValues.rate) / 100 / 12;
    const numberOfPayments = parseInt(calculatorValues.term) * 12;
    
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return monthlyPayment.toFixed(2);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Loans & Financing
              <span className="block text-3xl md:text-5xl bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                Dreams Within Reach
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-3xl mx-auto mb-8">
                              Whether you're buying a home, car, or funding personal projects, First Capital International 
              offers competitive rates, flexible terms, and personalized service to make your dreams affordable.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <FileText className="w-6 h-6 mr-2" />
                Open account to Apply
              </Link>
              <a
                href="#calculator"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-900 transition-all duration-300"
              >
                <Calculator className="w-6 h-6 mr-2" />
                Loan Calculator
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Loan Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Financing Solutions for Every Need
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Explore our comprehensive loan options designed to help you achieve your financial goals 
              with competitive rates, flexible terms, and exceptional service.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loanTypes.map((loan, index) => (
              <motion.div
                key={loan.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative group"
              >
                {loan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 h-full border-2 border-transparent hover:border-primary-200">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${loan.bgColor} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
                      <loan.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{loan.rate}</div>
                      <div className="text-sm text-gray-500">Starting rate</div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{loan.title}</h3>
                  <p className="text-gray-600 mb-6">{loan.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <span className="text-sm text-gray-500">Max Amount</span>
                      <div className="font-semibold text-gray-900">{loan.maxAmount}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Terms</span>
                      <div className="font-semibold text-gray-900">{loan.terms}</div>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {loan.features.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex space-x-3">
                    <Link
                      to="/register"
                      className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold text-center"
                    >
                      Open account to Apply
                    </Link>
                    <Link
                      to="/contact"
                      className="flex-1 border border-primary-600 text-primary-600 py-3 px-4 rounded-lg hover:bg-primary-50 transition-colors duration-300 font-semibold text-center"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty Loans Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Specialty Financing Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Specialized loan products designed for unique situations and specific financing needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {specialtyLoans.map((loan, index) => (
              <motion.div
                key={loan.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <loan.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{loan.title}</h3>
                <p className="text-gray-600 mb-3">{loan.description}</p>
                <div className="text-sm font-semibold text-green-600 mb-4">{loan.rate}</div>
                
                <ul className="space-y-1">
                  {loan.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section id="calculator" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Loan Payment Calculator
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Calculate your estimated monthly payments and see how much you can afford.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      value={calculatorValues.amount}
                      onChange={(e) => setCalculatorValues({...calculatorValues, amount: e.target.value})}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="250,000"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interest Rate (APR)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={calculatorValues.rate}
                      onChange={(e) => setCalculatorValues({...calculatorValues, rate: e.target.value})}
                      className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="6.25"
                    />
                    <span className="absolute right-3 top-3 text-gray-500">%</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Term (Years)
                  </label>
                  <select 
                    value={calculatorValues.term}
                    onChange={(e) => setCalculatorValues({...calculatorValues, term: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Select Term</option>
                    <option value="5">5 years</option>
                    <option value="10">10 years</option>
                    <option value="15">15 years</option>
                    <option value="20">20 years</option>
                    <option value="25">25 years</option>
                    <option value="30">30 years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Loan Type
                  </label>
                  <select 
                    value={calculatorValues.type}
                    onChange={(e) => setCalculatorValues({...calculatorValues, type: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Select Type</option>
                    <option value="mortgage">Home Mortgage</option>
                    <option value="auto">Auto Loan</option>
                    <option value="personal">Personal Loan</option>
                    <option value="home-equity">Home Equity</option>
                  </select>
                </div>
              </div>

              <div className="bg-primary-50 rounded-lg p-6 text-center">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Estimated Monthly Payment</h3>
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  ${calculatePayment() ? Number(calculatePayment()).toLocaleString() : '0.00'}
                </div>
                <p className="text-sm text-gray-600">*This is an estimate. Actual rates and payments may vary.</p>
              </div>

              <div className="text-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-300 font-semibold"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Open account to Apply
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Loan Services?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Experience the difference of working with a community bank that puts your needs first 
              and provides personalized service throughout your loan journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loanBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Simple Application Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Getting approved for your loan is easier than you think. Follow these simple steps to get started.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {applicationSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Customer Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Hear from customers who achieved their financial goals with our loan services.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-600 mb-6 italic">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-primary-600 font-medium">{testimonial.loanType}</div>
                  <div className="text-gray-500 text-sm flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {testimonial.location}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Take the first step toward achieving your financial goals. Apply online today or speak 
              with one of our loan specialists to find the perfect financing solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-600 bg-white rounded-xl hover:bg-gray-50 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                <FileText className="w-6 h-6 mr-2" />
                Open account to Apply
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-600 transition-colors duration-300"
              >
                <Phone className="w-6 h-6 mr-2" />
                Speak with Expert
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Loans; 